import { useState, useEffect } from "react";
import {
  FiX,
  FiSave,
  FiUser,
  FiPhone,
  FiGlobe,
  FiLinkedin,
  FiTwitter,
  FiInstagram,
  FiFacebook,
  FiYoutube,
} from "react-icons/fi";
import { IProfile } from "@/interfaces/Iprofile";
import { profileApi } from "@/api/profileApi";
import { ensurePhoneNumberFormat } from "@/utils/phoneNumberUtils";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileData: IProfile | null;
  onProfileUpdate: (updatedProfile: IProfile) => void;
}

const EditProfileModal = ({
  isOpen,
  onClose,
  profileData,
  onProfileUpdate,
}: EditProfileModalProps) => {
  const [formData, setFormData] = useState<IProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (profileData) {
      setFormData({ ...profileData });
    }
  }, [profileData]);

  const handleInputChange = (field: string, value: any) => {
    if (!formData) return;

    setFormData((prev) => {
      if (!prev) return prev;

      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        const parentValue = prev[parent as keyof IProfile] as any;
        return {
          ...prev,
          [parent]: {
            ...parentValue,
            [child]: value,
          },
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleArrayInputChange = (
    field: string,
    index: number,
    value: string
  ) => {
    if (!formData) return;

    const currentArray = formData[field as keyof IProfile] as string[];
    const updatedArray = [...currentArray];
    updatedArray[index] = value;

    setFormData((prev) => (prev ? { ...prev, [field]: updatedArray } : prev));
  };

  const addArrayItem = (field: string) => {
    if (!formData) return;

    const currentArray = formData[field as keyof IProfile] as string[];
    setFormData((prev) =>
      prev ? { ...prev, [field]: [...currentArray, ""] } : prev
    );
  };

  const removeArrayItem = (field: string, index: number) => {
    if (!formData) return;

    const currentArray = formData[field as keyof IProfile] as string[];
    const updatedArray = currentArray.filter((_, i) => i !== index);
    setFormData((prev) => (prev ? { ...prev, [field]: updatedArray } : prev));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData?.first_name?.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!formData?.last_name?.trim()) {
      newErrors.last_name = "Last name is required";
    }

    // Validate emails
    if (formData?.other_emails) {
      formData.other_emails.forEach((email, index) => {
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors[`email_${index}`] = "Invalid email format";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData || !validateForm()) return;

    try {
      setIsLoading(true);

      // Format phone numbers using the utility function
      const formattedData = {
        ...formData,
        phones: formData.phones.map((phone) => ensurePhoneNumberFormat(phone)),
      };

      console.log("📱 Original phones:", formData.phones);
      console.log("📱 Formatted phones:", formattedData.phones);

      const response = await profileApi.updateProfile(formattedData);

      if (response.data) {
        onProfileUpdate(response.data);
        onClose();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrors({ submit: "Failed to update profile. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !formData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FiUser className="mr-2" />
              Personal Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name || ""}
                  onChange={(e) =>
                    handleInputChange("first_name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1] ${
                    errors.first_name ? "border-red-500" : "border-gray-300"
                  }`}
                  aria-label="First name"
                />
                {errors.first_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.first_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name || ""}
                  onChange={(e) =>
                    handleInputChange("last_name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1] ${
                    errors.last_name ? "border-red-500" : "border-gray-300"
                  }`}
                  aria-label="Last name"
                />
                {errors.last_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.last_name}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                About
              </label>
              <textarea
                value={formData.about || ""}
                onChange={(e) => handleInputChange("about", e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                placeholder="Tell us about yourself..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <input
                type="text"
                value={formData.role || ""}
                onChange={(e) => handleInputChange("role", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                placeholder="Your role or position"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pronoun
              </label>
              <input
                type="text"
                value={formData.pronoun || ""}
                onChange={(e) => handleInputChange("pronoun", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                placeholder="e.g., he/him, she/her, they/them"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FiPhone className="mr-2" />
              Contact Information
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Numbers
              </label>

              {formData.phones.map((phone, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <div className="w-20">
                    <input
                      type="text"
                      value="+1"
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed text-center"
                      aria-label="Country code"
                    />
                  </div>
                  <input
                    type="tel"
                    value={phone || ""}
                    onChange={(e) =>
                      handleArrayInputChange("phones", index, e.target.value)
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                    placeholder="(123) 456-7890"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem("phones", index)}
                    className="px-3 py-2 text-red-500 hover:text-red-700"
                    aria-label={`Remove phone number ${index + 1}`}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem("phones")}
                className="text-[#78C7D1] hover:text-[#5ba8b3] text-sm"
              >
                + Add Phone Number
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Emails
              </label>
              {formData.other_emails.map((email, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="email"
                    value={email || ""}
                    onChange={(e) =>
                      handleArrayInputChange(
                        "other_emails",
                        index,
                        e.target.value
                      )
                    }
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1] ${
                      errors[`email_${index}`]
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="Email address"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem("other_emails", index)}
                    className="px-3 py-2 text-red-500 hover:text-red-700"
                    aria-label={`Remove email ${index + 1}`}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ))}
              {formData.other_emails.map(
                (_, index) =>
                  errors[`email_${index}`] && (
                    <p key={index} className="text-red-500 text-sm mt-1">
                      {errors[`email_${index}`]}
                    </p>
                  )
              )}
              <button
                type="button"
                onClick={() => addArrayItem("other_emails")}
                className="text-[#78C7D1] hover:text-[#5ba8b3] text-sm"
              >
                + Add Email
              </button>
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FiGlobe className="mr-2" />
              Social Links
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-3">
                <FiGlobe className="text-gray-400" />
                <input
                  type="url"
                  value={formData.social_links.website || ""}
                  onChange={(e) =>
                    handleInputChange("social_links.website", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                  placeholder="Website URL"
                />
              </div>

              <div className="flex items-center space-x-3">
                <FiLinkedin className="text-gray-400" />
                <input
                  type="url"
                  value={formData.social_links.linkedin || ""}
                  onChange={(e) =>
                    handleInputChange("social_links.linkedin", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                  placeholder="LinkedIn URL"
                />
              </div>

              <div className="flex items-center space-x-3">
                <FiTwitter className="text-gray-400" />
                <input
                  type="url"
                  value={formData.social_links.twitter || ""}
                  onChange={(e) =>
                    handleInputChange("social_links.twitter", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                  placeholder="Twitter URL"
                />
              </div>

              <div className="flex items-center space-x-3">
                <FiInstagram className="text-gray-400" />
                <input
                  type="url"
                  value={formData.social_links.instagram || ""}
                  onChange={(e) =>
                    handleInputChange("social_links.instagram", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                  placeholder="Instagram URL"
                />
              </div>

              <div className="flex items-center space-x-3">
                <FiFacebook className="text-gray-400" />
                <input
                  type="url"
                  value={formData.social_links.facebook || ""}
                  onChange={(e) =>
                    handleInputChange("social_links.facebook", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                  placeholder="Facebook URL"
                />
              </div>

              <div className="flex items-center space-x-3">
                <FiYoutube className="text-gray-400" />
                <input
                  type="url"
                  value={formData.social_links.youtube || ""}
                  onChange={(e) =>
                    handleInputChange("social_links.youtube", e.target.value)
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#78C7D1]"
                  placeholder="YouTube URL"
                />
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-[#78C7D1] hover:bg-[#5ba8b3] text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
